/**
 * /admin/ai-learning — N7 — per-feature correction rate + recent samples.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { getAILearningDashboard } from "../../api/admin.extras.api";
import { toArray } from "../../utils/toArray";

export default function AdminAILearning() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await getAILearningDashboard();
      // Normalise list fields at the setter so downstream .map/.length
      // calls don't crash when the API returns `null` / omits a key.
      // Each feature row's nested model_breakdown is also normalised.
      const payload = r.data || {};
      const feature_stats = toArray(payload.feature_stats).map((f) => ({
        ...f,
        model_breakdown: toArray(f?.model_breakdown),
      }));
      setData({
        ...payload,
        feature_stats,
        recent_corrections: toArray(payload.recent_corrections),
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load AI learning.");
    } finally {
      setLoading(false);
    }
  }

  if (loading && !data) {
    return <div className="p-6 text-text-secondary"><Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…</div>;
  }
  if (!data && error) {
    return (
      <div className="p-6">
        <div
          role="alert"
          className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center justify-between gap-2"
        >
          <span className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="truncate">{error}</span>
          </span>
          <button
            onClick={load}
            className="shrink-0 text-xs px-2 py-1 rounded border border-red-500/60 hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">AI Learning</h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-feature correction rate — higher = model is wrong more often.
            Active prompt revision is what production currently uses.
          </p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.feature_stats.map((f) => (
          <div key={f.code} className="rounded-md border border-border-default bg-bg-primary p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">{f.label}</h3>
              {f.active_revision_id ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white">
                  rev #{f.active_revision_id}
                </span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500 text-white">baseline</span>
              )}
            </div>
            <Row label="Total" value={f.total} />
            <Row label="Last 7d" value={f.last7} />
            <Row label="Last 30d" value={f.last30} />
            <Row label="Correction rate (30d)" value={`${f.correction_rate_30d}%`} />
            <div
              role="progressbar"
              aria-label={`${f.label} correction rate`}
              aria-valuenow={f.correction_rate_30d}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-1.5 bg-bg-secondary rounded mt-2 overflow-hidden"
            >
              <div
                style={{ width: `${Math.min(100, f.correction_rate_30d)}%` }}
                className="h-full bg-gradient-to-r from-emerald-500 to-rose-500"
              />
            </div>
            <Row label="Revisions" value={f.revision_count} />
            {f.model_breakdown.length > 0 && (
              <div className="text-[11px] text-text-secondary mt-2">
                Models (30d): {f.model_breakdown.map((m) => `${m.model_name || "—"}×${m.n}`).join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>

      <section className="mt-6">
        <h2 className="text-base font-semibold mb-2">Recent corrections (last 7d, sample)</h2>
        {data.recent_corrections.length === 0 ? (
          <p className="text-sm text-text-secondary">
            No corrections in the last 7 days. Either operators are accepting suggestions
            as-is, or no AI calls have happened yet.
          </p>
        ) : (
          <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
            <table className="w-full text-xs">
              <caption className="sr-only">Recent AI corrections sample</caption>
              <thead className="bg-bg-secondary text-left uppercase tracking-wider text-text-secondary">
                <tr>
                  <th scope="col" className="px-3 py-2">When</th>
                  <th scope="col" className="px-3 py-2">Feature</th>
                  <th scope="col" className="px-3 py-2">User</th>
                  <th scope="col" className="px-3 py-2">AI → Accepted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {data.recent_corrections.map((c) => (
                  <tr key={c.id} className="hover:bg-bg-secondary">
                    <td className="px-3 py-2 text-text-secondary">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
                    <td className="px-3 py-2 font-mono">{c.feature_code}</td>
                    <td className="px-3 py-2">{c.user_email || "—"}</td>
                    <td className="px-3 py-2">
                      <code className="text-[10px]">{JSON.stringify(c.ai_output).slice(0, 80)}</code>
                      {" → "}
                      <code className="text-[10px]">{JSON.stringify(c.accepted_output).slice(0, 80)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs my-0.5">
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}
